import type {
  CreateInvoiceRequest,
  Invoice,
  InvoiceDirection,
  InvoiceListResponse,
  InvoiceRecipientSearchResponse,
  InvoiceSortField,
  InvoiceStats,
  InvoiceStatus,
  SortOrder
} from '@tandemone/types'

import { api } from '@/api/client'

export type InvoiceListParams = {
  direction?: InvoiceDirection
  page?: number
  pageSize?: number
  status?: InvoiceStatus
  search?: string
  sort?: InvoiceSortField
  order?: SortOrder
}

export async function listInvoices(
  params: InvoiceListParams,
  signal?: AbortSignal
): Promise<InvoiceListResponse> {
  const { data } = await api.get<InvoiceListResponse>('/v1/invoices', {
    params,
    signal
  })
  return data
}

export async function getInvoiceStats(
  direction: InvoiceDirection,
  signal?: AbortSignal
): Promise<InvoiceStats> {
  const { data } = await api.get<InvoiceStats>('/v1/invoices/stats', {
    params: { direction },
    signal
  })
  return data
}

export async function getInvoice(
  id: string,
  signal?: AbortSignal
): Promise<Invoice> {
  const { data } = await api.get<Invoice>(`/v1/invoices/${id}`, { signal })
  return data
}

export async function searchInvoiceRecipients(
  query: string,
  signal?: AbortSignal
): Promise<InvoiceRecipientSearchResponse> {
  const { data } = await api.get<InvoiceRecipientSearchResponse>(
    '/v1/invoices/recipients',
    { params: { q: query }, signal }
  )
  return data
}

export async function createInvoice(
  body: CreateInvoiceRequest
): Promise<Invoice> {
  const { data } = await api.post<Invoice>('/v1/invoices', body)
  return data
}

export async function sendInvoice(id: string): Promise<Invoice> {
  const { data } = await api.post<Invoice>(`/v1/invoices/${id}/send`)
  return data
}

export async function markInvoicesPaid(
  ids: string[]
): Promise<{ updated: number }> {
  const { data } = await api.post<{ updated: number }>(
    '/v1/invoices/mark-paid',
    {
      ids
    }
  )
  return data
}

export async function cancelInvoice(id: string): Promise<Invoice> {
  const { data } = await api.post<Invoice>(`/v1/invoices/${id}/cancel`)
  return data
}

export async function getInvoicePdfUrl(
  id: string
): Promise<{ downloadUrl: string; expiresAt: string }> {
  const { data } = await api.get<{ downloadUrl: string; expiresAt: string }>(
    `/v1/invoices/${id}/pdf`
  )
  return data
}
